﻿import { SetStateAction, useEffect } from 'react';

import { PartnerDetailsContext, PartnersDefaultContext } from 'pages/partners/context';
import { NotificationContextProvider, useNotification } from 'app/notifications';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { partnerTypeNames, PartnerTypes } from 'apis/PartnerApi';
import { PartnerDetail } from 'pages/partners/PartnerDetail';
import { Language, LanguagesApi } from 'apis/LanguagesApi';
import { PartnerPermissions } from 'pages/partners/types';
import { setCountriesWithUnitedStateAtTop } from 'utils';
import { Country, CountryApi } from 'apis/CountryApi';
import { TabsContextProvider } from 'components/Tabs';
import { Partners } from 'pages/partners/Partners';
import { DefaultLayout } from 'components/layout';
import { State, StateApi } from 'apis/StateApi';
import { useParams } from 'react-router-dom';
import { getIdFromURL } from 'utils/other';
import { PermissionsApi } from 'apis';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';

export const PartnersWrapper = () => {

    const [state, setState] = useSuperState({
        countries: [] as Country[],
        languages: [] as Language[],
        loading: true,
        permissions: null as PartnerPermissions,
        states: [] as State[]
    });

    const { countries, languages, loading, permissions, states } = state;

    const { catchWithToast } = useNotification();
    const { formatMessage } = useIntl();

    const partnerType = (getIdFromURL('ptype')?.[0] ?? 0) as PartnerTypes | 0;

    const { id } = useParams();

    const setStates = (value: SetStateAction<State[]>) => {

        setState({ states: typeof value === 'function' ? value(state.states) : value });

    };

    const fetchPermissions = async () => {

        try {

            const permissions = await PermissionsApi.Get('Partner');
            setState({ loading: false, permissions });

        } catch (e) {

            console.error(e);
            setState({ loading: false });

        }

    };

    const loadData = async () => {

        try {

            const cou = await CountryApi.GetAll();
            setState({ countries: setCountriesWithUnitedStateAtTop(cou) });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_load_countries.6efc8543', defaultMessage: 'Failed to load countries.' }))(e);

        }

        try {

            const langs = await LanguagesApi.GetAll();
            setState({ languages: langs });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_load_languages.2aae1848', defaultMessage: 'Failed to load languages.' }))(e);

        }

        try {

            const val = await StateApi.GetAll(1);
            setState({ states: val });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_load_states.ec2a3c83', defaultMessage: 'Failed to load states.' }))(e);

        }

    };

    useEffect(() => {

        fetchPermissions();
        loadData();

    }, []);

    return (
        <DefaultLayout>
            <NotificationContextProvider>
                <PartnersDefaultContext.Provider
                    value={{
                        countries,
                        languages,
                        partner: {
                            name: partnerType === 0 ? '' : partnerTypeNames[partnerType - 1],
                            type: partnerType === 0 ? null : partnerType
                        },
                        permissions,
                        setStates,
                        states
                    }}
                >
                    <TabsContextProvider disableUrlSync context={PartnerDetailsContext}>
                        {loading ?
                            (
                                <div className={'d-flex align-items-center justify-content-center h-100'}>
                                    <GMetrixLoader />
                                </div>
                            )
                            :
                            (

                                // Key by partner type so switching types (same /partners route)
                                // remounts the page as if a new URL was hit, resetting all state.
                                id ? <PartnerDetail/> : <Partners key={partnerType}/>
                            )
                        }
                    </TabsContextProvider>
                </PartnersDefaultContext.Provider>
            </NotificationContextProvider>
        </DefaultLayout>
    );

};